import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MoveLogModal from '../components/MoveLogModal';
import { ukTodayKey, useUkTodayDefault } from '../utils/dates';
import './OnCall.css';

// Pay months are UK months (ONCALL_PAY_FIXES_DESIGN §8, commit B2). These read
// the browser's own calendar and then UTC, so a laptop abroad - or a UK one
// between midnight and 01:00 BST on the 1st - offered the wrong month.
function monthStart() {
  return `${ukTodayKey().slice(0, 7)}-01`;
}

function monthEnd() {
  const [year, month] = ukTodayKey().split('-').map(Number);
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

/**
 * Deleting a finalised snapshot un-pays every call-out it recorded, so the
 * confirmation says what is being given back and asks why (B3, §8). A month
 * finalised before line-by-line records cannot say what it paid, which is a
 * different - and more dangerous - thing to undo, so it is worded differently.
 */
function DeleteSnapshotModal({ snapshot, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const period = `${String(snapshot.report_start).slice(0, 10)} to ${String(snapshot.report_end).slice(0, 10)}`;
  const recorded = snapshot.ledger_status === 'recorded' || snapshot.ledger_status === 'backfilled';
  const logs = Number(snapshot.ledger_log_count || 0);
  const fees = Number(snapshot.paid_incident_count || 0);
  const ready = reason.trim().length >= 5;
  return (
    <div className="modal-overlay confirm-overlay" onClick={onCancel}>
      <div className="modal-content confirm-dialog" onClick={(event) => event.stopPropagation()}>
        <div className="confirm-body">
          <h3>Delete finalised snapshot?</h3>
          <p>
            {recorded
              ? `Deleting the snapshot for ${period} un-pays ${logs} work log${logs === 1 ? '' : 's'} and ${fees} call-out fee${fees === 1 ? '' : 's'}. They go back to being unpaid, and the next run for those days picks them up again.`
              : `The snapshot for ${period} was finalised before line-by-line records, so nothing here can say which call-outs it paid. Deleting it unlocks the period for corrections and re-finalising.`}
          </p>
          <label htmlFor="delete-snapshot-reason">Why is this being deleted?</label>
          <textarea
            id="delete-snapshot-reason"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="For example: September is being re-run after the duplicate ticket was fixed"
          />
          <p className="on-call-muted">The reason is stored with a copy of the snapshot and everything it paid.</p>
        </div>
        <div className="confirm-actions">
          <button type="button" className="confirm-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="confirm-danger" disabled={!ready} onClick={() => onConfirm(reason.trim())}>
            Delete snapshot
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Finance's answer for one call-out on the 'Check before paying late' list
 * (B4, Q18). Both answers move money - one pays it late, the other says it was
 * paid already and freezes it - so both are written down with a reason.
 */
function LogDecisionModal({ item, decision, onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const ready = reason.trim().length >= 5;
  const payingLate = decision === 'pay_late';
  return (
    <div className="modal-overlay confirm-overlay" onClick={onCancel}>
      <div
        className="modal-content confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={payingLate ? 'Pay this call-out late?' : 'Record this call-out as already paid?'}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="confirm-body">
          <h3>{payingLate ? 'Pay this call-out late?' : 'Record this call-out as already paid?'}</h3>
          <p>
            {item.full_name} · {item.reference || 'this job'} · {item.night_label}
          </p>
          <p className="on-call-muted">{item.why}</p>
          <p>
            {payingLate
              ? 'It will be paid in the next open pay period, marked late for its original night.'
              : 'It will never be paid again, and it can no longer be reopened or moved.'}
          </p>
          <label htmlFor="log-decision-reason">Why?</label>
          <textarea
            id="log-decision-reason"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={payingLate
              ? 'For example: the engineer closed it on 16 Sep, after August was finalised'
              : 'For example: checked against the August payslip - it was paid'}
          />
        </div>
        <div className="confirm-actions">
          <button type="button" className="confirm-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="on-call-btn primary" disabled={!ready} onClick={() => onConfirm(reason.trim())}>
            {payingLate ? 'Pay late in this period' : 'Already paid'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OnCallPayroll() {
  const { authFetch, user } = useAuth();
  const [activeTab, setActiveTab] = useState('reports');
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(monthEnd());
  const [report, setReport] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotToDelete, setSnapshotToDelete] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [coverTypes, setCoverTypes] = useState([]);
  const [salaryRows, setSalaryRows] = useState([]);
  const salaryEffectiveFrom = useUkTodayDefault();
  const [specialDayForm, setSpecialDayForm] = useState({ name: '', month_day: '' });
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  // Call-outs stranded on deleted tickets: not paid, and nothing carries them
  // forward, so Finance moves each one onto the ticket that was kept.
  const [strandedCallOuts, setStrandedCallOuts] = useState([]);
  const [strandedTruncated, setStrandedTruncated] = useState(false);
  const [moveLog, setMoveLog] = useState(null);
  // B4: Finance's decision on one reviewed call-out, and the line-by-line view
  // of what a finalised month paid.
  const [decisionFor, setDecisionFor] = useState(null);
  const [linesFor, setLinesFor] = useState(null);

  const fetchReport = useCallback(async () => {
    try {
      setError(null);
      const params = new URLSearchParams({ start, end });
      const response = await authFetch(`/api/v1/on-call/payroll?${params}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to fetch payroll report');
      setReport(data);
      const snapshotResponse = await authFetch('/api/v1/on-call/payroll/snapshots');
      if (snapshotResponse.ok) setSnapshots((await snapshotResponse.json()).data || []);
    } catch (err) {
      setError(err.message);
    }
  }, [authFetch, start, end]);

  const fetchStrandedCallOuts = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/tickets/deleted-with-call-outs');
      if (response.ok) {
        const payload = await response.json();
        setStrandedCallOuts(payload.data || []);
        setStrandedTruncated(Boolean(payload.truncated));
      }
    } catch (err) {
      // A rescue list that cannot be loaded must not take the payroll screen
      // down with it; the card simply does not appear.
      setStrandedCallOuts([]);
      setStrandedTruncated(false);
    }
  }, [authFetch]);

  const fetchSettings = useCallback(async () => {
    const [paymentRes, salaryRes] = await Promise.all([
      authFetch('/api/v1/on-call/admin/payment-settings'),
      authFetch('/api/v1/on-call/admin/engineer-salaries')
    ]);
    if (paymentRes.ok) {
      // cover_types only rides on the GET; the two PUTs answer with the plain
      // assumptions, so it lives apart from paymentSettings and survives a save.
      const { cover_types: types = [], ...settings } = await paymentRes.json();
      setCoverTypes(types);
      setPaymentSettings(settings);
    }
    if (salaryRes.ok) setSalaryRows((await salaryRes.json()).data || []);
  }, [authFetch]);

  // A retired type keeps its rate rows: its assigned days still pay from them.
  const isRetiredCoverType = (coverTypeId) => coverTypes.some(type => type.cover_type_id === coverTypeId && type.is_active === false);

  const finaliseReport = async () => {
    const notes = window.prompt('Finalisation notes') || '';
    const response = await authFetch('/api/v1/on-call/payroll/finalise', {
      method: 'POST',
      body: JSON.stringify({ start, end, notes })
    });
    const data = await response.json();
    if (!response.ok) {
      setError(data.message || 'Failed to finalise report');
      return;
    }
    setMessage('Payroll report finalised. Future rate or salary changes will not alter this snapshot.');
    fetchReport();
  };

  const performDeleteSnapshot = async (snapshot, reason) => {
    const period = `${String(snapshot.report_start).slice(0, 10)} to ${String(snapshot.report_end).slice(0, 10)}`;
    setSnapshotToDelete(null);
    const response = await authFetch(`/api/v1/on-call/payroll/snapshots/${snapshot.snapshot_id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason })
    });
    const data = await response.json();
    if (!response.ok) {
      setError(data.message || 'Failed to delete snapshot');
      return;
    }
    const released = data.released || {};
    const undone = Number(released.paid_logs || 0) + Number(released.paid_incidents || 0);
    setMessage(
      `Snapshot for ${period} deleted - the period can be corrected and re-finalised.`
      + (undone > 0 ? ` ${undone} paid line${undone === 1 ? '' : 's'} went back to unpaid.` : '')
    );
    fetchReport();
  };

  const recordDecision = async (item, decision, reason) => {
    setDecisionFor(null);
    const response = await authFetch('/api/v1/on-call/payroll/log-decisions', {
      method: 'POST',
      body: JSON.stringify({ log_id: item.log_id, decision, reason })
    });
    const data = await response.json();
    if (!response.ok) {
      setError(data.message || 'Failed to record the decision');
      return;
    }
    setMessage(data.message);
    fetchReport();
  };

  const showLines = async (snapshot) => {
    const response = await authFetch(`/api/v1/on-call/payroll/snapshots/${snapshot.snapshot_id}/lines`);
    const data = await response.json();
    if (!response.ok) {
      setError(data.message || 'Failed to fetch the paid lines');
      return;
    }
    setLinesFor(data);
  };

  /**
   * Save a finalised month's paid lines as a CSV (Q7).
   *
   * This has to go through authFetch. The API authenticates by
   * 'Authorization: Bearer' only - there is no cookie session - so a plain
   * <a href> download sends no token and comes back 401, and it would miss the
   * API_URL prefix as well.
   */
  const downloadLinesCsv = async (snapshot) => {
    try {
      const response = await authFetch(`/api/v1/on-call/payroll/snapshots/${snapshot.snapshot_id}/lines.csv`);
      if (!response.ok) {
        setError('Failed to download the paid lines');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `on-call-pay-${snapshot.report_start}-to-${snapshot.report_end}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download the paid lines');
    }
  };

  const savePaymentSettings = async () => {
    const response = await authFetch('/api/v1/on-call/admin/payment-settings', {
      method: 'PUT',
      body: JSON.stringify({
        incident_payment_gbp: paymentSettings.incident_payment_gbp,
        overtime_multiplier: paymentSettings.overtime_multiplier,
        engineer_payment_visibility: paymentSettings.engineer_payment_visibility,
        standard_day_hours: paymentSettings.overtime_rules?.standard_day_hours,
        lunch_break_minutes: paymentSettings.overtime_rules?.lunch_break_minutes,
        default_standard_start: paymentSettings.overtime_rules?.default_standard_start,
        ot_rounding_minutes: paymentSettings.overtime_rules?.ot_rounding_minutes
      })
    });
    if (response.ok) {
      setPaymentSettings(await response.json());
      setMessage('Payment settings saved');
    }
  };

  const saveRates = async () => {
    const response = await authFetch('/api/v1/on-call/admin/standby-rates', {
      method: 'PUT',
      body: JSON.stringify({ rates: paymentSettings.rates })
    });
    if (response.ok) {
      setPaymentSettings(await response.json());
      setMessage('Standby rates saved');
    }
  };

  const addSpecialDay = async (event) => {
    event.preventDefault();
    const response = await authFetch('/api/v1/on-call/admin/special-days', {
      method: 'POST',
      body: JSON.stringify(specialDayForm)
    });
    if (response.ok) {
      setSpecialDayForm({ name: '', month_day: '' });
      const settingsResponse = await authFetch('/api/v1/on-call/admin/payment-settings');
      if (settingsResponse.ok) setPaymentSettings(await settingsResponse.json());
      setMessage('Special day saved');
    }
  };

  const saveSalaries = async () => {
    const response = await authFetch('/api/v1/on-call/admin/engineer-salaries', {
      method: 'PUT',
      // Recomputed on the way out, so a screen open since before midnight
      // dates the rise today and not yesterday.
      body: JSON.stringify({ salaries: salaryRows, effective_from: salaryEffectiveFrom.refresh() })
    });
    if (response.ok) {
      setSalaryRows((await response.json()).data || []);
      setMessage('Engineer salaries saved');
    }
  };

  const canView = ['admin', 'manager', 'financial'].includes(user?.role);
  const canProcess = ['admin', 'financial'].includes(user?.role);
  // Gareth 2026-09-17: managers keep the salary figures up to date from the
  // rota section (API: ROLES.SALARY_EDIT). Finalising and deleting a paid
  // month stay with canProcess.
  const canEditSalaries = ['admin', 'manager', 'financial'].includes(user?.role);
  // Moving work between tickets is a MANAGEMENT action on the API (it moves
  // someone's paid hours and the customer's charge), so Finance sees the list
  // and asks a manager rather than being offered a button that 403s.
  const canMoveWork = ['admin', 'manager'].includes(user?.role);

  // Gareth 2026-08-04: every payroll value prints its working so the team
  // can re-do the arithmetic by hand.
  const gbp = (value) => `£${Number(value || 0).toFixed(2)}`;
  const otMultiplier = Number(report?.assumptions?.overtime_multiplier ?? 1.5);

  useEffect(() => {
    if (canView) {
      fetchReport();
      fetchSettings();
      fetchStrandedCallOuts();
    }
  }, [fetchReport, fetchSettings, fetchStrandedCallOuts, canView]);

  if (!canView) return <Navigate to="/" replace />;

  return (
    <div className="on-call-page">
      {decisionFor && (
        <LogDecisionModal
          item={decisionFor.item}
          decision={decisionFor.decision}
          onCancel={() => setDecisionFor(null)}
          onConfirm={(reason) => recordDecision(decisionFor.item, decisionFor.decision, reason)}
        />
      )}
      {snapshotToDelete && (
        <DeleteSnapshotModal
          snapshot={snapshotToDelete}
          onCancel={() => setSnapshotToDelete(null)}
          onConfirm={(reason) => performDeleteSnapshot(snapshotToDelete, reason)}
        />
      )}
      <div className="on-call-header">
        <div>
          <h1>On Call Payroll</h1>
          <p>Reports, rates, salaries, and payment settings.</p>
        </div>
        <div className="on-call-actions">
          {user?.role !== 'financial' && <Link className="on-call-btn secondary" to="/on-call">View Rota</Link>}
          {user?.role !== 'financial' && <Link className="on-call-btn secondary" to="/on-call/admin">Rota Admin</Link>}
        </div>
      </div>

      <div className="on-call-tabs">
        <button className={`on-call-tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
          Reports
        </button>
        <button className={`on-call-tab ${activeTab === 'rates' ? 'active' : ''}`} onClick={() => setActiveTab('rates')}>
          Rates & Settings
        </button>
        <button className={`on-call-tab ${activeTab === 'salaries' ? 'active' : ''}`} onClick={() => setActiveTab('salaries')}>
          Engineer Salaries
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {message && <div className="admin-info">{message}</div>}

      {moveLog && (
        <MoveLogModal
          ticketId={moveLog.ticket_id}
          ticketRef={moveLog.ticket_ref}
          log={moveLog}
          customerId={moveLog.customer_id}
          user={user}
          authFetch={authFetch}
          onClose={() => setMoveLog(null)}
          onSaved={(result) => {
            setMoveLog(null);
            setMessage(result?.message || 'The call-out was moved.');
            fetchStrandedCallOuts();
            fetchReport();
          }}
        />
      )}

      {activeTab === 'reports' && (
        <>
          <div className="on-call-card">
            <div className="on-call-controls">
              <label>Start
                <input type="date" value={start} onChange={e => setStart(e.target.value)} />
              </label>
              <label>End
                <input type="date" value={end} onChange={e => setEnd(e.target.value)} />
              </label>
              <button className="on-call-btn primary" onClick={fetchReport}>Run report</button>
              {canProcess && <button className="on-call-btn secondary" onClick={finaliseReport} disabled={!report}>Finalise snapshot</button>}
            </div>
            {/* Exactly which moments these figures pay for, in the API's own
                words: a month is not "the 1st to the 30th" to a database, and
                before November 2026 the months run on UTC days. */}
            {report?.pay_window?.text && (
              <p className="on-call-muted pay-window-line">{report.pay_window.text}</p>
            )}
            {/* B4: which of the three reports this is. A finalised month shows
                what was paid; a part-finalised range shows only the days that
                are not; an open one pays the late call-outs carried into it. */}
            {report?.banner && <p className="admin-info payroll-mode-banner">{report.banner}</p>}
          </div>

          <div className="on-call-card">
            <h2>Payroll Summary</h2>
            <table className="on-call-table">
              <thead>
                <tr>
                  <th>Engineer</th>
                  <th>Cover type</th>
                  <th>Day type</th>
                  <th>Assigned days</th>
                  <th>Standby rate</th>
                  <th>Standby payment</th>
                  <th>Annual salary</th>
                  <th>Hourly rate</th>
                </tr>
              </thead>
              <tbody>
                {(report?.data || []).map((row, index) => (
                  <tr key={`${row.user_id}-${row.cover_type_label}-${row.day_type}-${index}`}>
                    <td>{row.full_name}</td>
                    <td>{row.cover_type_label}</td>
                    <td>{row.day_type === 'weekday' ? 'Weekday' : row.day_type === 'weekend' ? 'Weekend' : 'Special Day'}</td>
                    <td>{row.assigned_days}</td>
                    <td>£{Number(row.standby_rate_gbp).toFixed(2)}</td>
                    <td>
                      £{Number(row.standby_payment_gbp).toFixed(2)}
                      <div className="calc-note">= {row.assigned_days} {row.assigned_days === 1 ? 'day' : 'days'} × {gbp(row.standby_rate_gbp)}</div>
                    </td>
                    <td>{row.annual_salary_gbp ? `£${Number(row.annual_salary_gbp).toFixed(2)}` : '-'}</td>
                    <td>
                      {row.calculated_hourly_rate_gbp ? `£${Number(row.calculated_hourly_rate_gbp).toFixed(2)}` : '-'}
                      {row.calculated_hourly_rate_gbp > 0 && (
                        <div className="calc-note">= {gbp(row.annual_salary_gbp)} ÷ 52 ÷ {Number(row.contracted_hours_per_week || 37.5)}h</div>
                      )}
                    </td>
                  </tr>
                ))}
                {(!report?.data || report.data.length === 0) && (
                  <tr><td colSpan="8">No assigned on-call cover in this period.</td></tr>
                )}
              </tbody>
            </table>
            {report && (
              <p className="on-call-muted">
                Total standby: £{Number(report.total_standby_gbp).toFixed(2)} (sum of the Standby payment column above)
              </p>
            )}
          </div>

          <div className="on-call-card">
            <h2>Call-out payments</h2>
            <p className="on-call-muted">
              Completed work logs flagged as on-call call-outs. Payment goes to the engineer who handled
              the call - not necessarily the standby engineer. Overtime counts only the out-of-hours
              portion: normal time runs {Number(report?.assumptions?.overtime_rules?.standard_day_hours ?? 7.5)}h
              + {Number(report?.assumptions?.overtime_rules?.lunch_break_minutes ?? 30)}min lunch elapsed from
              clock-in (or the engineer's standard start, default {report?.assumptions?.overtime_rules?.default_standard_start || '08:00'});
              time before the standard start and whole non-working days are always overtime.
              {Number(report?.assumptions?.overtime_rules?.ot_rounding_minutes ?? 15) > 0 && (
                <> Overtime hours round to the nearest {Number(report?.assumptions?.overtime_rules?.ot_rounding_minutes ?? 15)} minutes.</>
              )}{' '}
              Adjustable under Rates &amp; Settings.
            </p>
            <table className="on-call-table">
              <thead>
                <tr>
                  <th>Engineer</th>
                  <th>Call-outs</th>
                  <th>Incident pay</th>
                  <th>Hours worked</th>
                  <th>OOH hours</th>
                  <th>Hourly rate</th>
                  <th>Est. overtime</th>
                </tr>
              </thead>
              <tbody>
                {(report?.call_outs || []).map(row => (
                  <tr key={row.user_id}>
                    <td>{row.full_name}</td>
                    <td>{row.incident_count}</td>
                    <td>
                      £{Number(row.incident_payment_gbp).toFixed(2)}
                      {row.incident_count > 0 && (
                        <div className="calc-note">= {row.incident_count} × {gbp(row.incident_payment_gbp / row.incident_count)} fee</div>
                      )}
                    </td>
                    <td>{Number(row.worked_hours ?? row.overtime_hours).toFixed(2)}</td>
                    <td>{Number(row.overtime_hours).toFixed(2)}</td>
                    <td>
                      {row.salary_missing ? 'No salary set' : `£${Number(row.calculated_hourly_rate_gbp).toFixed(2)}`}
                      {!row.salary_missing && row.calculated_hourly_rate_gbp > 0 && (
                        <div className="calc-note">= {gbp(row.annual_salary_gbp)} ÷ 52 ÷ {Number(row.contracted_hours_per_week || 37.5)}h</div>
                      )}
                    </td>
                    <td>
                      £{Number(row.estimated_overtime_gbp).toFixed(2)}{row.salary_missing ? ' ⚠' : ''}
                      {!row.salary_missing && row.overtime_hours > 0 && (
                        <div className="calc-note">= {Number(row.overtime_hours).toFixed(2)}h × {gbp(row.calculated_hourly_rate_gbp)} × {otMultiplier}</div>
                      )}
                    </td>
                  </tr>
                ))}
                {(!report?.call_outs || report.call_outs.length === 0) && (
                  <tr><td colSpan="7">No completed call-outs in this period.</td></tr>
                )}
              </tbody>
            </table>
            {report && (
              <p className="on-call-muted">
                <strong>Grand total: £{Number(report.total_gbp || 0).toFixed(2)}</strong>
                {' '}= {gbp(report.total_standby_gbp)} standby
                + {gbp(report.total_incident_gbp)} incidents
                + {gbp(report.total_estimated_overtime_gbp)} est. overtime
              </p>
            )}
            {(report?.call_outs || []).some(row => row.salary_missing) && (
              <p className="admin-error">⚠ Some engineers have no annual salary set - their overtime shows £0.00. Set salaries in the Engineer Salaries tab.</p>
            )}
          </div>

          {(report?.late_lines || []).length > 0 && (
            <div className="on-call-card">
              <h2>Late call-outs paid in this period</h2>
              <p className="on-call-muted">
                These call-outs started in a pay period that was already finalised and were never paid by
                it. They are paid here, at the rates in force on the night they happened. The money is
                already included in the figures above - it is not extra to them.
              </p>
              <table className="on-call-table">
                <thead>
                  <tr>
                    <th>Engineer</th>
                    <th>Ticket or job</th>
                    <th>Night</th>
                    <th>Late for</th>
                    <th>Fee</th>
                    <th>OOH hours</th>
                    <th>Overtime</th>
                  </tr>
                </thead>
                <tbody>
                  {report.late_lines.map((line, index) => (
                    <tr key={`${line.engineer_id}-${line.parent_key}-${line.night}-${index}`}>
                      <td>{line.full_name}</td>
                      <td>{line.reference || '-'}</td>
                      <td>{line.night_day_label || line.night}</td>
                      <td>{line.label}</td>
                      <td>{gbp(line.fee_gbp)}</td>
                      <td>{Number(line.ooh_hours || 0).toFixed(2)}</td>
                      <td>{gbp(line.overtime_gbp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(report?.skipped_fees || []).length > 0 && (
            <div className="on-call-card">
              <h2>Call-out fees not paid again</h2>
              <p className="on-call-muted">
                One night's disturbance is one call-out fee, whichever pay period pays it.
              </p>
              <ul>
                {report.skipped_fees.map((skip, index) => (
                  <li key={`${skip.parent_key}-${skip.night}-${skip.engineer_id}-${index}`}>
                    {skip.full_name ? `${skip.full_name}: ` : ''}{skip.note}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(report?.review_items || []).length > 0 && (
            <div className="on-call-card">
              <h2>Check before paying late</h2>
              <p className="on-call-muted">
                These call-outs sit inside months finalised before call-outs were recorded line by line,
                so nothing here can say whether they were paid. Nothing is paid on a guess: decide each
                one, with a reason.
              </p>
              <table className="on-call-table">
                <thead>
                  <tr>
                    <th>Engineer</th>
                    <th>Ticket or job</th>
                    <th>Night</th>
                    <th>Period</th>
                    <th>Why</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {report.review_items.map((item) => (
                    <tr key={item.log_id}>
                      <td>{item.full_name}</td>
                      <td>{item.reference || '-'}</td>
                      <td>{item.night_label}</td>
                      <td>{item.period_label}</td>
                      <td>{item.why}</td>
                      <td>
                        {canProcess ? (
                          <>
                            <button
                              className={`on-call-btn ${item.suggested_decision === 'pay_late' ? 'primary' : 'secondary'}`}
                              onClick={() => setDecisionFor({ item, decision: 'pay_late' })}
                            >
                              {/* The money always goes to the next OPEN period.
                                  On a finalised month 'this period' is the one
                                  being looked at, which is the one period that
                                  will NOT pay it. */}
                              {report.mode === 'open' ? 'Pay late in this period' : 'Pay late in the next open period'}
                            </button>
                            {' '}
                            <button
                              className="on-call-btn secondary"
                              onClick={() => setDecisionFor({ item, decision: 'already_paid' })}
                            >
                              Already paid
                            </button>
                          </>
                        ) : (
                          <span className="on-call-muted">Ask Finance to decide</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(report?.times_changed_after_payment || []).length > 0 && (
            <div className="on-call-card">
              <h2>Times changed after payment</h2>
              <p className="on-call-muted">
                Someone was already paid for this work and its times have moved since. The figures stay as
                they were paid - nothing here is paid again automatically.
              </p>
              <ul>
                {report.times_changed_after_payment.map((changed) => (
                  <li key={changed.log_id}>
                    {changed.full_name}: {changed.reference || 'this job'} · {changed.note}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {report?.since_finalised && (report.since_finalised.carried.length > 0
            || report.since_finalised.due.length > 0
            || report.since_finalised.review > 0) && (
            <div className="on-call-card">
              <h2>Since finalising</h2>
              <ul>
                {report.since_finalised.carried.map((row, index) => (
                  <li key={`carried-${index}`}>
                    {row.full_name}: {row.kind === 'fee' ? 'call-out fee' : 'call-out hours'} for {row.night_label} —{' '}
                    {gbp(row.amount_gbp)} paid late in {row.paid_in_period}
                  </li>
                ))}
                {report.since_finalised.due.map((row) => (
                  <li key={`due-${row.log_id}`}>
                    {row.full_name}: {row.reference || 'this job'} · {row.night_label} — {row.note}
                  </li>
                ))}
                {/* The rest of this month's unpaid call-outs are not forgotten:
                    they are on the decision list, which is the only place they
                    can be settled. Without this line the card reads as though
                    nothing else is outstanding. */}
                {report.since_finalised.review > 0 && (
                  <li>
                    {report.since_finalised.review} more{' '}
                    {report.since_finalised.review === 1 ? 'call-out is' : 'call-outs are'}{' '}
                    waiting for Finance to decide — see &lsquo;Check before paying late&rsquo;
                  </li>
                )}
              </ul>
            </div>
          )}

          {strandedCallOuts.length > 0 && (
            <div className="on-call-card">
              <h2>Call-outs on deleted tickets (not paid)</h2>
              <p className="on-call-muted">
                The pay engine leaves out work logged against a deleted ticket, so these call-outs pay
                the engineer nothing. Move each one onto the ticket that was kept and it is paid in the
                normal way. A ticket holding a call-out can no longer be deleted, so this list only
                grows from tickets deleted before that rule, or straight in the database.
                {' '}A call-out that was already paid in a finalised period is marked here and cannot be
                moved — delete that snapshot first if it genuinely needs correcting.
              </p>
              {strandedTruncated && (
                <p className="on-call-muted">
                  Showing the most recent {strandedCallOuts.length}. There are more; move these and
                  reload to see the rest.
                </p>
              )}
              <table className="on-call-table">
                <thead>
                  <tr>
                    <th>Engineer</th>
                    <th>Night</th>
                    <th>Deleted ticket</th>
                    <th>Customer</th>
                    <th>State</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {strandedCallOuts.map(row => (
                    <tr key={row.log_id}>
                      <td>{row.engineer_name}</td>
                      <td>{row.night_label}</td>
                      <td>{row.ticket_ref}</td>
                      <td>{row.customer_name || '-'}</td>
                      <td>{row.state_label}</td>
                      <td>
                        {row.movable === false ? (
                          <span className="on-call-muted">
                            Already paid in {row.paid_period} — cannot be moved
                          </span>
                        ) : canMoveWork ? (
                          <button className="on-call-btn secondary" onClick={() => setMoveLog(row)}>
                            Move to another ticket
                          </button>
                        ) : (
                          <span className="on-call-muted">Ask a manager to move this</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="on-call-card">
            <h2>Finalised snapshots</h2>
            <table className="on-call-table">
              <thead><tr><th>Period</th><th>Finalised</th><th>By</th><th>Standby</th><th>Incidents</th><th>Est. overtime</th><th>Total</th><th>Notes</th><th /></tr></thead>
              <tbody>
                {snapshots.map(snapshot => (
                  <tr key={snapshot.snapshot_id}>
                    <td>{String(snapshot.report_start).slice(0, 10)} to {String(snapshot.report_end).slice(0, 10)}</td>
                    <td>{new Date(snapshot.finalised_at).toLocaleString()}</td>
                    <td>{snapshot.finalised_by_name || '-'}</td>
                    <td>£{Number(snapshot.total_standby_gbp).toFixed(2)}</td>
                    <td>£{Number(snapshot.total_incident_gbp || 0).toFixed(2)}</td>
                    <td>£{Number(snapshot.total_estimated_overtime_gbp || 0).toFixed(2)}</td>
                    <td>
                      £{(Number(snapshot.total_standby_gbp) + Number(snapshot.total_incident_gbp || 0) + Number(snapshot.total_estimated_overtime_gbp || 0)).toFixed(2)}
                      <div className="calc-note">= standby + incidents + overtime</div>
                      {/* B4 (US2): late pay is part of that total, so it is a
                          note under it and not a column of its own. */}
                      {Number(snapshot.late_count || 0) > 0 && (
                        <div className="calc-note">
                          includes £{Number(snapshot.late_total_gbp || 0).toFixed(2)} late ({snapshot.late_count} item{Number(snapshot.late_count) === 1 ? '' : 's'})
                        </div>
                      )}
                    </td>
                    <td>
                      {snapshot.notes || '-'}
                      {snapshot.ledger_status === 'unreconciled' && (
                        <div className="calc-note">Finalised before line-by-line records</div>
                      )}
                    </td>
                    <td>
                      <button className="on-call-btn secondary" onClick={() => showLines(snapshot)}>Lines</button>
                      {' '}
                      <button className="on-call-btn secondary" onClick={() => downloadLinesCsv(snapshot)}>CSV</button>
                      {canProcess ? (
                        <>
                          {' '}
                          <button className="on-call-btn secondary" onClick={() => setSnapshotToDelete(snapshot)}>Delete</button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {snapshots.length === 0 && <tr><td colSpan="9">No finalised reports yet.</td></tr>}
              </tbody>
            </table>
          </div>

          {linesFor && (
            <div className="on-call-card">
              <div className="on-call-controls">
                <h2>What {linesFor.snapshot.period} paid, line by line</h2>
                <button className="on-call-btn secondary" onClick={() => setLinesFor(null)}>Close</button>
              </div>
              {linesFor.snapshot.ledger_status === 'unreconciled' ? (
                <p className="on-call-muted">
                  This period was finalised before line-by-line records, so it cannot say which call-outs
                  it paid.
                </p>
              ) : (
                <table className="on-call-table">
                  <thead>
                    <tr>
                      <th>Engineer</th>
                      <th>Ticket or job</th>
                      <th>Customer</th>
                      <th>Night</th>
                      <th>Type</th>
                      <th>Fee</th>
                      <th>OOH hours</th>
                      <th>Overtime</th>
                      <th>Late for</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linesFor.data.map((line, index) => (
                      <tr key={`${line.kind}-${line.log_id || line.parent_key}-${index}`}>
                        <td>{line.full_name}</td>
                        <td>{line.reference || '-'}</td>
                        <td>{line.customer_name || '-'}</td>
                        <td>{line.night_label}</td>
                        <td>{line.kind === 'fee' ? 'Call-out fee' : 'Call-out hours'}</td>
                        <td>{line.fee_gbp === null ? '-' : gbp(line.fee_gbp)}</td>
                        <td>{line.ooh_hours === null ? '-' : Number(line.ooh_hours).toFixed(2)}</td>
                        <td>{line.overtime_gbp === null ? '-' : gbp(line.overtime_gbp)}</td>
                        <td>{line.original_period ? `${line.late_label} (${line.original_period})` : '-'}</td>
                      </tr>
                    ))}
                    {linesFor.data.length === 0 && <tr><td colSpan="9">No call-outs were paid in this period.</td></tr>}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'rates' && paymentSettings && (
        <>
          <div className="on-call-card">
            <h2>Payment settings</h2>
            <div className="on-call-form-row">
              <label>Incident payment (superseded)
                <input type="number" min="0" step="0.01" value={paymentSettings.incident_payment_gbp} disabled
                  title="Superseded: the incident payment is now the call-out fee in Settings → Rates (effective-dated, per-engineer overridable)." />
                <small>Now configured as the call-out fee in Settings &rarr; Rates.</small>
              </label>
              <label>Overtime multiplier
                <input type="number" min="0" step="0.01" value={paymentSettings.overtime_multiplier} onChange={e => setPaymentSettings(prev => ({ ...prev, overtime_multiplier: e.target.value }))} />
              </label>
              <label className="on-call-checkbox">
                <input type="checkbox" checked={Boolean(paymentSettings.engineer_payment_visibility)} onChange={e => setPaymentSettings(prev => ({ ...prev, engineer_payment_visibility: e.target.checked }))} />
                Show engineers their own payment summary
              </label>
            </div>
            <h3 style={{ margin: '16px 0 4px', fontSize: '14px' }}>Overtime rules</h3>
            <p className="on-call-muted">
              Normal time runs (standard day hours + lunch break) elapsed from the engineer's clock-in
              (or their standard start, else the default below). Time before the standard start and
              non-working days are always overtime.
            </p>
            <div className="on-call-form-row">
              <label>Standard day hours
                <input type="number" min="0.5" max="24" step="0.25" value={paymentSettings.overtime_rules?.standard_day_hours ?? 7.5} onChange={e => setPaymentSettings(prev => ({ ...prev, overtime_rules: { ...prev.overtime_rules, standard_day_hours: e.target.value } }))} />
              </label>
              <label>Lunch break (minutes)
                <input type="number" min="0" max="240" step="5" value={paymentSettings.overtime_rules?.lunch_break_minutes ?? 30} onChange={e => setPaymentSettings(prev => ({ ...prev, overtime_rules: { ...prev.overtime_rules, lunch_break_minutes: e.target.value } }))} />
              </label>
              <label>Default start time
                <input type="time" value={paymentSettings.overtime_rules?.default_standard_start ?? '08:00'} onChange={e => setPaymentSettings(prev => ({ ...prev, overtime_rules: { ...prev.overtime_rules, default_standard_start: e.target.value } }))} />
              </label>
              <label>OT rounding (minutes)
                <input type="number" min="0" max="60" step="5" value={paymentSettings.overtime_rules?.ot_rounding_minutes ?? 15} onChange={e => setPaymentSettings(prev => ({ ...prev, overtime_rules: { ...prev.overtime_rules, ot_rounding_minutes: e.target.value } }))} />
                <small>Overtime rounds to the nearest this many minutes. 0 = no rounding.</small>
              </label>
              {canProcess && <button className="on-call-btn primary" onClick={savePaymentSettings}>Save payment settings</button>}
            </div>
          </div>

          <div className="on-call-card">
            <h2>Standby rates</h2>
            <p className="on-call-muted">Daily rates paid to engineers for on-call standby duties.</p>
            <table className="on-call-table">
              <thead><tr><th>Cover type</th><th>Day type</th><th>Rate GBP</th></tr></thead>
              <tbody>{paymentSettings.rates.map((rate, index) => (
                <tr key={`${rate.cover_type_id}-${rate.day_type}`}>
                  <td>{rate.cover_type_label}{isRetiredCoverType(rate.cover_type_id) ? ' (Retired)' : ''}</td>
                  <td>{rate.day_type === 'weekday' ? 'Weekday' : rate.day_type === 'weekend' ? 'Weekend' : 'Special Day'}</td>
                  <td><input type="number" min="0" step="0.01" value={rate.amount_gbp} onChange={e => setPaymentSettings(prev => ({ ...prev, rates: prev.rates.map((item, itemIndex) => itemIndex === index ? { ...item, amount_gbp: e.target.value } : item) }))} /></td>
                </tr>
              ))}</tbody>
            </table>
            {canProcess && <button className="on-call-btn primary" onClick={saveRates}>Save standby rates</button>}
          </div>

          <div className="on-call-card">
            <h2>Special days</h2>
            <p className="on-call-muted">Days that use weekend/special rates instead of weekday rates.</p>
            <table className="on-call-table">
              <thead><tr><th>Name</th><th>Month/day</th><th>Active</th></tr></thead>
              <tbody>{paymentSettings.special_days.map(day => <tr key={day.special_day_id}><td>{day.name}</td><td>{day.month_day}</td><td>{day.is_active ? 'Yes' : 'No'}</td></tr>)}</tbody>
            </table>
            <form className="on-call-form-row" onSubmit={addSpecialDay}>
              <label>Name
                <input value={specialDayForm.name} onChange={e => setSpecialDayForm(prev => ({ ...prev, name: e.target.value }))} required />
              </label>
              <label>Month/day
                <input placeholder="MM-DD" value={specialDayForm.month_day} onChange={e => setSpecialDayForm(prev => ({ ...prev, month_day: e.target.value }))} required />
              </label>
              {canProcess && <button className="on-call-btn primary" type="submit">Add special day</button>}
            </form>
          </div>
        </>
      )}

      {activeTab === 'salaries' && (
        <div className="on-call-card">
          <h2>Engineer salaries</h2>
          <p className="on-call-muted">
            Hourly rate = annual salary ÷ 52 ÷ the engineer's contracted hours/week (37.5 when unset —
            set contracted hours on the engineer's profile). Salary changes are effective-dated:
            saving appends a new row from the chosen date; history and past payroll are never rewritten.
          </p>
          <label className="on-call-muted">Effective from{' '}
            <input type="date" value={salaryEffectiveFrom.value} onChange={e => salaryEffectiveFrom.setValue(e.target.value)} />
          </label>
          <table className="on-call-table">
            <thead><tr><th>Engineer</th><th>Contracted hrs/wk</th><th>Annual salary GBP</th><th>In force since</th><th>Calculated hourly rate</th></tr></thead>
            <tbody>{salaryRows.map((row, index) => (
              <tr key={row.user_id}>
                <td>{row.full_name}</td>
                <td>{row.contracted_hours_per_week || '37.5 (default)'}</td>
                <td><input type="number" min="0" step="0.01" value={row.annual_salary_gbp || ''} onChange={e => setSalaryRows(prev => prev.map((item, itemIndex) => itemIndex === index ? { ...item, annual_salary_gbp: e.target.value } : item))} /></td>
                <td>{row.effective_from ? String(row.effective_from).slice(0, 10) : '—'}</td>
                <td>{row.calculated_hourly_rate_gbp ? `£${Number(row.calculated_hourly_rate_gbp).toFixed(2)}` : '-'}</td>
              </tr>
            ))}</tbody>
          </table>
          {canEditSalaries && <button className="on-call-btn primary" onClick={saveSalaries}>Save salaries</button>}
        </div>
      )}
    </div>
  );
}

export default OnCallPayroll;
